import { createContext, useCallback, useContext, useSyncExternalStore } from 'react';

/**
 * Zugang zu den Vorjahreswerten (D-17) fuer die geteilten Feldkomponenten -- der Zwilling
 * von `fieldWarnings.ts`.
 *
 * Warum als Kontext und nicht als Prop: dieselbe Begruendung wie bei `FieldWarningsContext`
 * (siehe dort) -- vier von fuenf Formularen zeigten die gelbe Warnstufe nie, weil niemand sie
 * als Prop verdrahtet hatte. Eine Feldkomponente kennt ihren Feldnamen ohnehin; holt sie sich
 * ihren Vorwert selbst, greift die Anzeige in allen fuenf Formularen ohne deren Zutun und ohne
 * handgepflegte Feldlisten.
 *
 * Der Kontexttyp ist bewusst strukturell (`get`/`subscribe`) und nicht der importierte
 * `PreviousValuesStore` aus `components/reports/formStores.ts`: `components/form` ist die
 * allgemeinere Ebene und soll nicht auf `components/reports` zurueckzeigen. Der vorhandene
 * Store passt ohne Anpassung in diese Form.
 */
export interface PreviousValueSource {
  get(): Record<string, string>;
  subscribe(listener: () => void): () => void;
}

export const PreviousValuesContext = createContext<PreviousValueSource | null>(null);

const NOOP_SUBSCRIBE = () => () => {};

/**
 * Liest den Vorwert genau EINES Feldes.
 *
 * Der Schnappschuss ist der Wert-Text selbst, nicht das Vorwert-Objekt -- exakt dieselbe
 * Begruendung wie bei `useFieldWarning` (`fieldWarnings.ts:27-34`): der externe Store wird
 * mit `Object.is` verglichen und rendert eine Feldkomponente nur dann neu, wenn sich IHR
 * Wert geaendert hat. Bei Formular D mit 280 Feldern ist das der Unterschied zwischen
 * unmerklich und Ruckler.
 *
 * Der `NOOP_SUBSCRIBE`-Zweig ist Pflicht: eine Feldkomponente ausserhalb eines
 * `PreviousValuesContext.Provider` (Test, Storybook, kuenftige Wiederverwendung) bekommt
 * `undefined`, statt abzustuerzen.
 */
export function usePreviousValue(field: string): string | undefined {
  const source = useContext(PreviousValuesContext);

  const getSnapshot = useCallback(
    () => (source ? source.get()[field] : undefined),
    [source, field]
  );

  return useSyncExternalStore(source ? source.subscribe : NOOP_SUBSCRIBE, getSnapshot);
}
